import { Modal, ModalProps } from 'antd';
import LoadMore from 'components/LoadMore';
import dayjs from 'lib/dayjs';
import { HTMLAttributes, useState } from 'react';
import { unstable_batchedUpdates as batchedUpdates } from 'react-dom';
import { useTranslate } from '@tolgee/react';
import styled from '@emotion/styled';
import Empty from 'components/Empty';
import { logList } from './api';

export interface LogProps extends HTMLAttributes<HTMLDivElement> {
  log: Aide.TaskLog;
}

export function Log(props: LogProps) {
  const {
    log: { opName, action, taskName, opAt },
    ...otherProps
  } = props;

  const { t } = useTranslate();

  const actionLabelMap = {
    CREATE: t('marketing.新增'),
    EDIT: t('marketing.编辑'),
    START: t('aide.启动'),
    STOP: t('aide.结束'),
  };

  return (
    <div {...otherProps}>
      <div>{`${opName} ${actionLabelMap[action]} [${taskName}]`}</div>
      <span>{dayjs(opAt).format('YYYY-MM-DD HH:mm:ss')}</span>
    </div>
  );
}

const ListWrapper = styled.ul<{ border?: boolean }>`
  margin: 0;
  padding: 0;
  list-style: none;
  border-left: ${({ border = true }) => (border ? 'dashed' : 'none')} 1px var(--fett-border-strong);
  > li {
    position: relative;
    padding: 0 0 16px 10px;
    &::before {
      display: inline-block;
      content: '';
      position: absolute;
      top: 7px;
      left: -5px;
      width: 9px;
      height: 9px;
      border-radius: 9px;
      background: #d3d7e0;
    }
    &:first-of-type::after {
      display: inline-block;
      content: '';
      position: absolute;
      top: 0;
      left: -1px;
      width: 1px;
      height: 7px;
      background: var(--fett-bg-panel);
      z-index: 1;
    }
    &:last-of-type::after {
      display: inline-block;
      content: '';
      position: absolute;
      top: 16px;
      left: -1px;
      width: 1px;
      height: 100%;
      background: var(--fett-bg-panel);
      z-index: 1;
    }
    > div {
      color: var(--fett-text-secondary);
      > span {
        color: var(--fett-text-tertiary);
      }
    }
  }
`;

export interface LogListProps extends HTMLAttributes<HTMLDivElement> {
  platform: General.PlatformUpperCase;
  scenarioType: Marketing.AllScenarioType;
}

const pageSize = 10;

export function LogList(props: LogListProps) {
  const { platform, scenarioType, ...otherProps } = props;

  const [list, setList] = useState<Aide.TaskLog[]>([]);

  const [pageNum, setPageNum] = useState(0);

  const [total, setTotal] = useState(Infinity);

  const hasMore = list.length < total;

  return (
    <LoadMore
      hasMore={hasMore}
      onLoadMore={() => {
        return logList({
          scenarioType,
          platform,
          pageNum: pageNum + 1,
          pageSize,
        }).then((res) => {
          const {
            pages: { pageNum, totalRows },
            data,
          } = res;

          batchedUpdates(() => {
            setList((pre) => [...pre, ...data]);
            setPageNum(pageNum);
            setTotal(totalRows);
          });
        });
      }}
    >
      {(loadEle) => {
        if (!hasMore && list.length === 0) {
          return <Empty />;
        }

        return (
          <div {...otherProps}>
            <ListWrapper border={list.length > 1}>
              {list.map((log) => (
                <li>
                  <Log log={log} />
                </li>
              ))}
            </ListWrapper>
            {loadEle}
          </div>
        );
      }}
    </LoadMore>
  );
}

const StickyModal = styled(Modal)`
  .ant-modal-header {
    position: sticky;
    top: 0;
    z-index: 1;
  }
`;

export interface LogListModalProps extends ModalProps {
  platform: General.PlatformUpperCase;
  scenarioType: Marketing.AllScenarioType;
}

export function LogListModal(props: LogListModalProps) {
  const { platform, scenarioType, ...otherProps } = props;

  const { t } = useTranslate();

  return (
    <StickyModal title={t('aide.操作日志')} width={440} footer={null} {...otherProps}>
      <LogList key={platform} platform={platform} scenarioType={scenarioType} />
    </StickyModal>
  );
}
